import { useCallback, useEffect, useState } from 'react';
import { STYLE_ID } from './constants';
import { isTawkHost, isTawkIframe } from './dom';
import { cancelTawkLayout, layoutTawkIframes, pulseTawkLayout, scheduleTawkLayout } from './layout';
import { callTawk, syncVisibility, tawk } from './session';
import { ensureTawkApi, ensureVisitorEmail, loadTawkScript, syncTawkVisitor } from './api';
import { closeTawkChat, openTawkChat } from './visibility';
import { applyTheme, applyThemeToIframe, collectAddedTawkIframes, readTheme } from './theme';
import { themeCss } from './themeCss';

export function useTawkEmbed(themeRootRef) {
  const [chatOpen, setChatOpen] = useState(false);

  const openTeamChat = useCallback(() => {
    setChatOpen(true);
    openTawkChat();
  }, []);

  useEffect(() => {
    tawk.mountCount += 1;
    tawk.wantVisible = false;
    const api = ensureTawkApi();
    const previousOnLoad = api.onLoad;
    const previousOnChatMaximized = api.onChatMaximized;
    const previousOnChatMinimized = api.onChatMinimized;
    const themeRoot = () => themeRootRef?.current || null;
    tawk.notifyClosed = () => setChatOpen(false);

    const iframeStyleObservers = [];
    const watchedIframes = new WeakSet();

    const watchIframe = (iframe) => {
      if (watchedIframes.has(iframe)) return;
      watchedIframes.add(iframe);
      const styleObserver = new MutationObserver(scheduleTawkLayout);
      styleObserver.observe(iframe, { attributes: true, attributeFilter: ['style', 'class'] });
      iframeStyleObservers.push(styleObserver);
      iframe.addEventListener('load', scheduleTawkLayout);
    };

    const paint = () => {
      syncVisibility();
      syncTawkVisitor(api);
      if (tawk.mountCount > 0) applyTheme(themeRoot());
      layoutTawkIframes();
      scheduleTawkLayout();
      if (tawk.pendingMaximize && tawk.mountCount > 0) openTawkChat();
    };

    api.onLoad = function () {
      if (typeof previousOnLoad === 'function') previousOnLoad();
      paint();
    };

    api.onChatMaximized = function () {
      if (typeof previousOnChatMaximized === 'function') previousOnChatMaximized();
      tawk.wantVisible = true;
      tawk.pendingMaximize = false;
      setChatOpen(true);
      pulseTawkLayout();
    };

    api.onChatMinimized = function () {
      if (typeof previousOnChatMinimized === 'function') previousOnChatMinimized();
      tawk.wantVisible = false;
      tawk.pendingMaximize = false;
      setChatOpen(false);
      callTawk('hideWidget');
      layoutTawkIframes();
    };

    const emailAbort = new AbortController();
    const startWidget = () => {
      if (emailAbort.signal.aborted) return;
      syncTawkVisitor(api);
      loadTawkScript();
      paint();
    };

    paint();
    Promise.race([
      ensureVisitorEmail(emailAbort.signal),
      new Promise((resolve) => window.setTimeout(resolve, 2500)),
    ]).finally(startWidget);

    const observer = new MutationObserver((mutations) => {
      if (tawk.mountCount <= 0) return;
      const css = themeCss(readTheme(themeRoot()));
      const added = collectAddedTawkIframes(mutations);
      for (const tawkFrame of added) {
        applyThemeToIframe(tawkFrame, css);
        tawkFrame.addEventListener('load', () => applyThemeToIframe(tawkFrame, css));
        watchIframe(tawkFrame);
      }
      if (added.length) {
        applyTheme(themeRoot());
        scheduleTawkLayout();
      }
    });

    if (document.body) observer.observe(document.body, { childList: true, subtree: true });

    document.querySelectorAll('iframe').forEach((iframe) => {
      if (isTawkIframe(iframe) || isTawkHost(iframe.parentElement)) watchIframe(iframe);
    });

    const raf = window.requestAnimationFrame(() => {
      applyTheme(themeRoot());
      layoutTawkIframes();
    });

    const onKeyDown = (event) => {
      if (event.key !== 'Escape' || !tawk.wantVisible) return;
      closeTawkChat();
    };
    const onViewport = () => scheduleTawkLayout();
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onViewport);
    window.addEventListener('orientationchange', onViewport);
    window.visualViewport?.addEventListener('resize', onViewport);
    window.visualViewport?.addEventListener('scroll', onViewport);

    return () => {
      emailAbort.abort();
      tawk.mountCount -= 1;
      tawk.wantVisible = false;
      tawk.pendingMaximize = false;
      tawk.notifyClosed = null;
      window.cancelAnimationFrame(raf);
      cancelTawkLayout();
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onViewport);
      window.removeEventListener('orientationchange', onViewport);
      window.visualViewport?.removeEventListener('resize', onViewport);
      window.visualViewport?.removeEventListener('scroll', onViewport);
      observer.disconnect();
      iframeStyleObservers.forEach((so) => so.disconnect());
      document.documentElement.classList.remove('tawk-chat-open');
      syncVisibility();
      if (tawk.mountCount <= 0) document.getElementById(STYLE_ID)?.remove();
    };
  }, [themeRootRef]);

  return { chatOpen, openTeamChat };
}
